import React from 'react';
import { History, Compass, FileText, Lightbulb, Plus } from 'lucide-react';

// ── 首页功能按钮（呼吸矩阵下方的大图标竖排按钮） ──────────────────────
// 卡文急救 / 章节摘要 / 灵感点子 / 会话历史 + 主入口「新建会话」。
// 「提示词」按钮**不在**这一组：首页没有输入框，提示词无处可落，
// 它按新形态留在会话模式底部输入区（与输入框、模型选择按钮同行）。
//
// 布局：3 列等宽 + 自动换行 → 5 个按钮正好排成 3 + 2 两行（第二行居中），
// 高度确定（2 × 56px + 8px 行距 = 120px），首页矩阵的停靠位移常量依赖这个高度
// （见 ai-panel.css 的 --ink-matrix-home-drop）。
//
// 卡片/对话态不再收窄按钮行：工具对话一开就已经进入会话模式，会话模式下本行不渲染。

/** 大图标：20–24px 区间取 22px；细线笔触与面板 1px 细线语法同源 */
const ICON_SIZE = 22;
const ICON_STROKE = 1.5;

interface AIAssistantToolsBarProps {
  model: string;
  hasChapter: boolean;
  historyOpen?: boolean;
  onToggleHistory?: () => void;
  onPlot: () => void;
  onSummary: () => void;
  onInspiration: () => void;
  /** 主入口：进入会话模式（只切形态，不发送消息；会话记录由首条消息创建） */
  onNewConversation: () => void;
}

export function AIAssistantToolsBar({
  model, hasChapter,
  historyOpen, onToggleHistory,
  onPlot, onSummary, onInspiration,
  onNewConversation,
}: AIAssistantToolsBarProps) {
  const noModel = !model;

  return (
    <div className="ai-rw-home-actions">
      {/* 主入口：1px 主色细线 + 主色图标（不做发光/渐变） */}
      <button
        className="ai-tools-btn ai-home-action ai-home-action--primary"
        onClick={onNewConversation}
        title="进入会话模式，开始一轮新的 AI 对话"
      >
        <Plus size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        <span className="ai-home-action-label">新建会话</span>
      </button>

      {/* 以下 4 个按钮各自带一个类别修饰类（ai-panel.css 按类给悬停色）：
          静态仍是同一个墨色，只有悬停 / 聚焦 / 展开时才透出各自的色相。
          类名与语义：--plot 赭石（卡住了）/ --summary 花青（正文）/ 
          --inspiration 藤黄（灵光）/ --history 黛（导航）。 */}
      <button
        className="ai-tools-btn ai-home-action ai-home-action--plot"
        onClick={onPlot}
        disabled={noModel || !hasChapter}
        title={!hasChapter ? '请先打开一个章节' : (noModel ? '请先选择模型' : '基于大纲与本章结尾，进入对话生成情节走向')}
      >
        <Compass size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        <span className="ai-home-action-label">卡文急救</span>
      </button>

      <button
        className="ai-tools-btn ai-home-action ai-home-action--summary"
        onClick={onSummary}
        disabled={noModel || !hasChapter}
        title={!hasChapter ? '请先打开一个章节' : (noModel ? '请先选择模型' : '进入对话为当前章节生成摘要（确认后写入摘要字段）')}
      >
        <FileText size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        <span className="ai-home-action-label">章节摘要</span>
      </button>

      <button
        className="ai-tools-btn ai-home-action ai-home-action--inspiration"
        onClick={onInspiration}
        disabled={noModel}
        title={noModel ? '请先选择模型' : '基于大纲进入对话生成灵感点子（纯参考，不写入作品）'}
      >
        <Lightbulb size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        <span className="ai-home-action-label">灵感点子</span>
      </button>

      <button
        className={`ai-tools-btn ai-home-action ai-home-action--history ai-tools-history-btn${historyOpen ? ' is-active' : ''}`}
        onClick={onToggleHistory}
        title="查看全部会话历史"
      >
        <History size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        <span className="ai-home-action-label">会话历史</span>
      </button>
    </div>
  );
}
